import type { ReactNode } from 'react';
import { Button } from '../../../components/Button';
import { SkeletonLines } from '../../../components/Skeleton';
import { formatDateTime } from '../../../lib/format';
import { useExamSessions } from '../api/exam-sessions-api';
import styles from './Assessments.module.css';

/**
 * F9.09 from outside the session: pick one of the job's scheduled sessions with a seat.
 * Used by a results row's "Book a session" and the pipeline's "Add to exam session".
 */
export function BookSessionPanel({
  jobId,
  who,
  needed,
  onPick,
  onNewSession,
  onCancel,
}: {
  readonly jobId: string;
  /** "Rahim Uddin", or "12 people". */
  readonly who: string;
  /** Seats wanted, to flag a session too small. */
  readonly needed: number;
  readonly onPick: (sessionId: string, sessionName: string) => void;
  readonly onNewSession?: () => void;
  readonly onCancel: () => void;
}): ReactNode {
  const sessions = useExamSessions({ when: 'upcoming', jobId });
  const open = (sessions.data ?? []).filter((session) => session.status === 'scheduled');

  return (
    <div className={styles.confirm}>
      <p className={styles.confirmTitle}>Book {who} on a session</p>
      {sessions.isPending ? (
        <SkeletonLines count={3} />
      ) : open.length === 0 ? (
        <p className={styles.confirmBody}>This job has no scheduled session taking bookings. A locked list takes nobody new.</p>
      ) : (
        <div className={styles.pool}>
          {open.map((session) => {
            const left = session.capacity - session.booked;
            return (
              <div key={session.id} className={styles.poolItem}>
                <div className={styles.poolText}>
                  <strong>{session.name}</strong>
                  <span className={styles.meta}>
                    {session.startsAt ? formatDateTime(session.startsAt) : 'No date'} · {session.venue ?? 'No venue'} · {left} seat{left === 1 ? '' : 's'} left
                  </span>
                </div>
                <Button size="sm" variant="outline" disabled={left <= 0} onClick={() => onPick(session.id, session.name)}>
                  {needed > left && left > 0 ? `Book (${left} fit)` : 'Book'}
                </Button>
              </div>
            );
          })}
        </div>
      )}
      <p className={styles.confirmBody}>Each person booked is emailed the date and place.</p>
      <div className={styles.confirmActions}>
        <Button variant="ghost" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        {onNewSession && (
          <Button variant="outline" size="sm" onClick={onNewSession}>
            New session
          </Button>
        )}
      </div>
    </div>
  );
}
